// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Mail, UserPlus, AlertCircle, Loader2, ChevronDown } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ApiError, request } from '@/lib/api'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: () => void
}

type Status = 'idle' | 'submitting' | 'error'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Roles an org admin can invite. SSO-native: no password is set here — the
// member binds on first federated login (POST /v1/auth/bootstrap).
const ROLES = ['provider', 'clinic_staff', 'org_admin'] as const
type InviteRole = (typeof ROLES)[number]

export default function AddProviderModal({ open, onOpenChange, onCreated }: Props) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<InviteRole>('provider')
  const [status, setStatus] = useState<Status>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  function reset() {
    setEmail('')
    setRole('provider')
    setStatus('idle')
    setErrorMsg('')
  }

  function handleOpenChange(val: boolean) {
    onOpenChange(val)
    if (!val) reset()
  }

  async function handleSubmit() {
    if (!EMAIL_RE.test(email.trim())) {
      setErrorMsg(t('providers.emailRequired'))
      setStatus('error')
      return
    }
    setStatus('submitting')
    setErrorMsg('')
    try {
      await request('/v1/org/members', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), role }),
      })
      onCreated()
      handleOpenChange(false)
    } catch (err) {
      // 409 = already a member; everything else = generic create error.
      const msg = err instanceof ApiError && err.status === 409
        ? t('providers.memberExists')
        : t('providers.createError')
      setErrorMsg(msg)
      setStatus('error')
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent title={t('providers.addProvider')} className="max-w-md">
        <div className="w-full h-px mb-5 bg-gradient-to-r from-transparent via-[#006EF9]/60 to-transparent" />
        <p className="text-sm text-muted-foreground -mt-1 mb-5">{t('providers.modalSubtitle')}</p>

        {status === 'error' && (
          <div className="mb-4 flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-destructive/8 border border-destructive/20">
            <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
            <span className="text-sm text-destructive">{errorMsg}</span>
          </div>
        )}

        <div className="flex flex-col gap-4">
          {/* Email */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {t('providers.email')} <span className="text-destructive">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                type="email"
                className="pl-10"
                placeholder="provider@your-clinic.org"
                value={email}
                onChange={e => { setEmail(e.target.value); if (status === 'error') setStatus('idle') }}
              />
            </div>
          </div>

          {/* Role */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="invite-role" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {t('providers.roleLabel')}
            </label>
            <div className="relative">
              <select
                id="invite-role"
                value={role}
                onChange={(e) => setRole(e.target.value as InviteRole)}
                className="w-full h-10 appearance-none rounded-lg border border-border bg-card px-3 pr-9 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>{t(`providers.role_${r}`)}</option>
                ))}
              </select>
              <ChevronDown className="h-4 w-4 text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            <p className="text-xs text-muted-foreground">{t('providers.inviteHint')}</p>
          </div>

          <Button
            size="full"
            disabled={status === 'submitting'}
            onClick={() => void handleSubmit()}
            className="mt-1 bg-gradient-to-r from-[#006EF9] to-[#02C7C3] hover:opacity-90 text-white border-0"
          >
            {status === 'submitting' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('providers.creating')}
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4" />
                {t('providers.create')}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
